import React, { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { toast } from "../ui/sonner";
import { fmtD } from "../../lib/date";
import { Loader2, RefreshCw, Ban } from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
const TYPE_CLS = { Invoice: "text-blue-700", Estimate: "text-slate-500", Payment: "text-emerald-700", Deposit: "text-emerald-600", "Credit Memo": "text-amber-700", Refund: "text-red-600" };

export function QbLedgerReview({ api, zh, previewId, row, canAdjust, onAdjusted }) {
  const [ledger, setLedger] = useState(null);
  const [ex, setEx] = useState(new Set(row.excluded || []));
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let alive = true;
    api.get(`/projects/import/qb/preview/${previewId}/rows/${encodeURIComponent(row.key)}`).then(({ data }) => { if (alive) { setLedger(data.ledger || []); setEx(new Set(data.excluded || [])); } }).catch(() => alive && setLedger([]));
    return () => { alive = false; };
  }, [api, previewId, row.key]);
  const dirty = ledger && (ex.size !== (row.excluded || []).length || [...ex].some((i) => !(row.excluded || []).includes(i)));
  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/import/qb/preview/${previewId}/adjust`, { key: row.key, exclude: [...ex] });
      toast.success(zh ? `已重算：A/R ${money(row.ar)} → ${money(data.row.ar)}${data.row.needsReview ? "" : "（不再需复核）"}` : `Recalculated: A/R ${money(data.row.ar)}`);
      onAdjusted?.(data.row, data.summary);
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "重算失败" : "Failed")); }
    finally { setBusy(false); }
  };
  if (!ledger) return <p className="text-slate-400 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" />{zh ? "读取 QB 明细…" : "Loading…"}</p>;
  return (
    <div data-testid={`qb-ledger-${row.key}`}>
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <p className="font-semibold text-slate-700">{zh ? `QB 交易明细（${ledger.length} 笔）` : `QB transactions (${ledger.length})`}</p>
        {canAdjust && <span className="text-slate-400">{zh ? "勾选重复 / 退票 / 重复入账的交易后「重算」，A/R 与收款计划会按排除后的数据重新生成" : "Tick duplicate / bounced transactions, then recalculate"}</span>}
        {canAdjust && <Button size="sm" variant={dirty ? "default" : "outline"} className={`h-6 text-[11px] ml-auto ${dirty ? "bg-amber-600 hover:bg-amber-700 text-white" : ""}`} disabled={!dirty || busy} onClick={apply} data-testid={`qb-adjust-${row.key}`}>{busy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}{zh ? `按排除重算（排除 ${ex.size} 笔）` : `Recalculate (${ex.size} excluded)`}</Button>}
      </div>
      <div className="max-h-72 overflow-auto rounded border border-slate-200 bg-white">
        <table className="w-full text-[11px]">
          <thead className="bg-slate-50 text-slate-500 sticky top-0"><tr>{[canAdjust ? (zh ? "排除" : "Excl.") : "", "#", zh ? "日期" : "Date", zh ? "类型" : "Type", "Num", "Memo", zh ? "账户" : "Account", zh ? "金额" : "Amount"].map((h, i) => <th key={i} className="text-left px-2 py-1 font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
          <tbody>
            {ledger.map((t) => {
              const off = ex.has(t.idx);
              const receipt = ["Payment", "Deposit", "Refund", "Credit Memo"].includes(t.type);
              return (
                <tr key={t.idx} className={`border-t border-slate-100 ${off ? "bg-red-50/60 text-slate-400 line-through" : ""}`} data-testid={`qb-txn-${row.key}-${t.idx}`} data-excluded={off ? "1" : "0"}>
                  <td className="px-2 py-0.5">{canAdjust && receipt && <input type="checkbox" checked={off} onChange={() => setEx((s) => { const n = new Set(s); n.has(t.idx) ? n.delete(t.idx) : n.add(t.idx); return n; })} data-testid={`qb-exclude-${row.key}-${t.idx}`} />}{canAdjust && !receipt && <Ban className="h-3 w-3 text-slate-200" />}</td>
                  <td className="px-2 py-0.5 text-slate-400">{t.idx}</td>
                  <td className="px-2 py-0.5 whitespace-nowrap">{fmtD(t.date)}</td>
                  <td className={`px-2 py-0.5 whitespace-nowrap font-medium ${off ? "" : TYPE_CLS[t.type] || ""}`}>{t.type}</td>
                  <td className="px-2 py-0.5 text-slate-500">{t.num}</td>
                  <td className="px-2 py-0.5 max-w-[260px] truncate" title={t.memo}>{t.memo}</td>
                  <td className="px-2 py-0.5 text-slate-500 whitespace-nowrap">{t.account}</td>
                  <td className="px-2 py-0.5 text-right whitespace-nowrap font-medium">{money(t.amount)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
